import classNames from 'classnames';
import { NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import type { ReactElement, ReactNode, Ref, RefObject } from 'react';
import { useRef } from 'react';
import styles from './TextSlide.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './TextSlide.transitions';
import type { TextSlideProps, TextSlideRef } from './TextSlide.data';
import Heading from '../../../atoms/heading/Heading';
import {
  useMountTransition,
  useTransitionController,
  useUnMountTransition,
} from '../../../../util/react-transition-component';
import { useChangeTransitionState } from '../../../../util/react-transition-component/hooks/useChangeTransitionState';
import Text from '../../../atoms/text/Text';

export default function TextSlide({
  isVisible,
  transitionOnMount = false,
  transitionOnUnMount = false,
  ref,
  ...props
}: TextSlideProps & { ref?: Ref<TextSlideRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const topLeftTitleRef = useRef<HTMLHeadingElement>(null);
  const bottomRightTitleRef = useRef<HTMLHeadingElement>(null);
  const contentFrameRef = useRef<HTMLDivElement>(null);
  const kanjiTextRef = useRef<HTMLParagraphElement>(null);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        topLeftTitleRef,
        bottomRightTitleRef,
        kanjiTextRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useMountTransition(transitionController, transitionOnMount);
  useUnMountTransition(transitionController, transitionOnUnMount);
  useChangeTransitionState(transitionController, isVisible);

  return (
    <div ref={elementRef} className={classNames(styles.textSlide, props.className)}>
      <div ref={contentFrameRef} className={classNames(styles.contentFrame, 'grid-gutter')}>
        <Heading
          ref={topLeftTitleRef}
          as="h3"
          size="heading-01"
          className={classNames(styles.topLeftTitle)}
          transitionOutType="splitCharsSlideOutUp"
        >
          {render(props.blok.topLeftTitle, {
            nodeResolvers: {
              [NODE_PARAGRAPH]: (children: ReactNode) => (
                <>
                  {children}
                  <br />
                </>
              ),
            },
          })}
        </Heading>

        <Heading
          ref={bottomRightTitleRef}
          as="h3"
          size="heading-01"
          className={classNames(styles.bottomRightTitle)}
          transitionOutType="splitCharsSlideOutUp"
        >
          {render(props.blok.bottomRightTitle, {
            nodeResolvers: {
              [NODE_PARAGRAPH]: (children: ReactNode) => (
                <>
                  {children}
                  <br />
                </>
              ),
            },
          })}
        </Heading>

        <Text
          ref={kanjiTextRef}
          as="span"
          size="kanji-medium"
          className={classNames(styles.kanjiText)}
          transitionInType="slideUpFadeIn"
          lang="ja"
        >
          {props.blok.kanjiText}
        </Text>
      </div>
    </div>
  );
}
